import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type PointSummary } from '../lib/api';
import { Badge, Button, Card, EmptyState, Section, Skeleton, Switch } from '../components/ui';
import { PLATFORM_LABEL, relativeDate } from '../lib/format';
import { alert, haptic } from '../lib/telegram';
import { useSession } from '../hooks/session';

const STATUS_META: Record<string, { label: string; color: string; hint: string | null }> = {
  ACTIVE: { label: 'работает', color: 'var(--color-positive)', hint: null },
  PENDING: { label: 'подключается', color: 'var(--color-neutral)', hint: null },
  NEEDS_2FA: {
    label: 'нужен доступ',
    color: 'var(--color-neutral)',
    hint: 'Доступ к площадке прервался. Напишите разработчику — восстановим.',
  },
  INVALID_CREDENTIALS: {
    label: 'нет доступа',
    color: 'var(--color-negative)',
    hint: 'Ключ интеграции больше не подходит. Напишите разработчику.',
  },
  BLOCKED: {
    label: 'заблокировано',
    color: 'var(--color-negative)',
    hint: 'Площадка временно закрыла доступ. Обычно проходит само; если нет — напишите разработчику.',
  },
  DISABLED: {
    label: 'отключено',
    color: 'var(--color-hint)',
    hint: 'Сбор остановлен после серии ошибок. Напишите разработчику.',
  },
};

/**
 * Экран площадок.
 *
 * Собран вокруг точки, а не вокруг подключения: владелец думает «кофейня на
 * Тургенева», а не «аккаунт 2ГИС». Одна точка живёт сразу на нескольких
 * площадках, поэтому площадки показаны внутри точки, а не отдельным списком —
 * раньше одно и то же кафе выглядело двумя несвязанными строками.
 *
 * Подключение новых точек отсюда убрано намеренно: это работа разработчика, и
 * мастер с ключами доступа владельцу только мешал.
 */
export function AccountsPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: api.accounts });
  const points = useQuery({ queryKey: ['points'], queryFn: api.points });
  const billing = useQuery({ queryKey: ['billing'], queryFn: api.billing });

  const sync = useMutation({
    mutationFn: async () => {
      const list = accounts.data?.accounts ?? [];
      await Promise.all(list.map((account) => api.syncAccount(account.id)));
    },
    onSuccess: () => {
      haptic.success();
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['points'] });
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
    },
    onError: (error: Error) => {
      haptic.error();
      alert(error.message);
    },
  });

  const setWatched = useMutation({
    mutationFn: ({ key, watched }: { key: string; watched: boolean }) => api.setPointWatched(key, watched),
    /*
     * Список правим сразу, не дожидаясь ответа: ожидание на каждом щелчке
     * читается как «не сработало». При ошибке откатываем.
     */
    onMutate: async ({ key, watched }) => {
      haptic.tap();
      await queryClient.cancelQueries({ queryKey: ['points'] });
      const previous = queryClient.getQueryData<{ points: PointSummary[] }>(['points']);
      queryClient.setQueryData<{ points: PointSummary[] }>(['points'], (current) =>
        current
          ? { points: current.points.map((item) => (item.key === key ? { ...item, watched } : item)) }
          : current,
      );
      return { previous };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(['points'], context.previous);
      haptic.error();
      alert(error.message);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['points'] }),
  });

  if (accounts.isLoading || points.isLoading) {
    return (
      <div className="mx-auto max-w-lg space-y-2 p-4 pt-6">
        <Skeleton className="h-28" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  const list = accounts.data?.accounts ?? [];
  const items = points.data?.points ?? [];
  const contact = (billing.data?.supportContact ?? 'thiskai').replace(/^@/, '');

  if (list.length === 0 || items.length === 0) {
    return (
      <EmptyState
        icon="🗺"
        title="Точки не подключены"
        description="Точки подключает разработчик — напишите, и они появятся здесь."
        action={<DeveloperLink contact={contact} label="Написать разработчику" />}
      />
    );
  }

  const watched = items.filter((item) => item.watched).length;
  const reviews = items.reduce((sum, item) => sum + item.reviews, 0);
  // Показываем самое свежее обновление среди площадок: владельцу важно «когда
  // система последний раз смотрела», а не состояние каждого подключения.
  const lastSync = list
    .map((account) => account.lastSyncAt)
    .filter(Boolean)
    .map((value) => new Date(value as string | Date).toISOString())
    .sort()
    .at(-1);

  const troubled = list
    .map((account) => ({ account, meta: STATUS_META[account.status] }))
    .filter((row) => row.meta?.hint);

  return (
    <div className="mx-auto max-w-lg pt-3 pb-8">
      <Section title="Сбор отзывов">
        <Card>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="t-title-2 tabular-nums">
                {watched}
                <span className="t-body text-[var(--color-hint)]"> из {items.length}</span>
              </p>
              <p className="mt-0.5 t-footnote text-[var(--color-hint)]">точек под наблюдением</p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              {list.map((account) => {
                const meta = STATUS_META[account.status] ?? STATUS_META['DISABLED']!;
                return (
                  <Badge key={account.id} color={meta.color}>
                    {PLATFORM_LABEL[account.platform]} · {meta.label}
                  </Badge>
                );
              })}
            </div>
          </div>

          <p className="mt-3 t-footnote text-[var(--color-hint)]">
            {reviews} {plural(reviews, 'отзыв', 'отзыва', 'отзывов')} собрано
            {lastSync ? ` · обновлено ${relativeDate(lastSync)}` : ' · ещё не обновлялось'}
          </p>

          {can.sync && (
            <Button
              variant="secondary"
              className="mt-3 w-full"
              loading={sync.isPending}
              onClick={() => sync.mutate()}
            >
              Обновить сейчас
            </Button>
          )}
        </Card>

        {/* Проблему объясняем словами: цветной ярлык не говорит, что делать. */}
        {troubled.map(({ account, meta }) => (
          <Card key={account.id} className="mt-2">
            <p className="t-subhead font-medium">
              {PLATFORM_LABEL[account.platform]}: {meta!.label}
            </p>
            <p className="mt-1 t-footnote leading-relaxed text-[var(--color-hint)]">{meta!.hint}</p>
          </Card>
        ))}
      </Section>

      <Section
        title="Точки"
        footer={
          can.managePlatforms
            ? 'Выключенная точка не опрашивается и не попадает в ленту. Собранные отзывы остаются.'
            : undefined
        }
      >
        <Card className="p-0">
          <ul className="divide-y divide-[var(--color-separator)]">
            {items.map((point) => (
              <PointRow
                key={point.key}
                point={point}
                canManage={can.managePlatforms}
                onToggle={(next) => setWatched.mutate({ key: point.key, watched: next })}
              />
            ))}
          </ul>
        </Card>
      </Section>

      <Section title="Новая точка">
        <Card>
          <p className="t-footnote leading-relaxed text-[var(--color-hint)]">
            Чтобы добавить точку или площадку, напишите разработчику — подключим, и она появится здесь.
          </p>
          <DeveloperLink contact={contact} label="Добавить точку" className="mt-3 w-full" />
        </Card>
      </Section>
    </div>
  );
}

function PointRow({
  point,
  canManage,
  onToggle,
}: {
  point: PointSummary;
  canManage: boolean;
  onToggle: (watched: boolean) => void;
}) {
  return (
    <li className={point.watched ? '' : 'opacity-55'}>
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate t-callout font-medium">{point.name}</p>
          {point.address && (
            <p className="mt-0.5 truncate t-footnote text-[var(--color-hint)]">{point.address}</p>
          )}

          <div className="mt-2 flex flex-wrap gap-1.5">
            {point.platforms.map((item) => (
              <span
                key={item.id}
                className="t-caption inline-flex items-center gap-1 rounded-full bg-[var(--color-surface)] px-2 py-[3px] whitespace-nowrap"
              >
                <span className="font-medium">{PLATFORM_LABEL[item.platform]}</span>
                <span className="tabular-nums text-[var(--color-hint)]">{item.reviews}</span>
                {item.rating !== null && (
                  <span className="tabular-nums text-[var(--color-hint)]">· {item.rating.toFixed(1)}★</span>
                )}
              </span>
            ))}
          </div>
        </div>

        {canManage && (
          <div className="shrink-0 pt-0.5">
            <Switch checked={point.watched} onChange={onToggle} label={`Следить за точкой ${point.name}`} />
          </div>
        )}
      </div>
    </li>
  );
}

/** Единственный путь добавить точку — к разработчику. */
function DeveloperLink({
  contact,
  label,
  className,
}: {
  contact: string;
  label: string;
  className?: string;
}) {
  return (
    <a
      className={`pressable inline-flex h-11 items-center justify-center rounded-[var(--radius-control)] bg-[var(--color-accent)] px-4 t-body font-medium text-white ${className ?? ''}`}
      href={`https://t.me/${contact}`}
      target="_blank"
      rel="noreferrer"
      onClick={() => haptic.tap()}
    >
      {label}
    </a>
  );
}

/** Русские окончания для счётчиков: «1 отзыв», «2 отзыва», «5 отзывов». */
function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = count % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
